import type { ComponentProps } from 'react';
import { cn, ScrollArea as HeritageScrollArea } from '@herit-fe/heritage-ui';

// overscroll 체이닝 차단 — 뷰포트 끝 도달 시 body/html 로 스크롤이 넘어가(바운스) 배경이 밀리는 것 방지.
// 스크롤 영역이면 표면 모양과 무관하게 항상 옳아 **기본 적용**한다.
// heritage ScrollArea 는 className 을 Root 로만 넘기므로 자손(뷰포트/스크롤바)을 셀렉터로 잡는다.
// **`**:data-[slot=…]` 형태 필수** — `[&_[data-slot=x]]`(따옴표 없는 원시 attribute 셀렉터)는 v4 파서가
// CSS 를 못 만들 때가 있다(클래스는 DOM 에 붙지만 규칙 미생성). `data-` variant 는 값을 따옴표로 감싸 안정.
const OVERSCROLL_CONTAIN = '**:data-[slot=scroll-area-viewport]:overscroll-contain';
const OVERSCROLL_AUTO = '**:data-[slot=scroll-area-viewport]:overscroll-auto!';

// 오버레이 스크롤바 인셋(**축별 opt-in**) — 트랙 여백(py/px)으로 thumb 를 모서리에서, 가장자리 margin(mr/mb)
// 으로 보더 선에서 뗀다. 인셋 8px = rounded-xl(16px) 모서리를 대체로 비껴감.
//
// **축을 나눈 이유**: 세로바가 걸리는 모서리(상하)와 가로바가 걸리는 모서리(좌우)는 별개다. 한 영역이 한 축만
// 스크롤하는 경우가 대부분이라, 축을 묶으면 안 쓰는 축의 여백까지 딸려온다.
//
// **기본값이 아닌 이유**: 겹침은 스크롤 영역이 `rounded-*` 보더 컨테이너에 꽉 찰 때만 생기는 문제다. 전역
// 기본으로 두면 모서리가 없는 영역(시트 본문·탭 바 등)에서 스크롤바만 잘린 듯 어색해진다 — 실제로 그렇게
// 전 스크롤 영역에 번져서 opt-in 으로 뒤집었다.
// ⚠️ Tailwind 는 소스를 **문자열 스캔**해 클래스를 생성한다 — 셀렉터 접두사를 변수로 빼 조합(`${V}py-2`)하면
//    스캐너가 후보를 못 찾아 CSS 가 생성되지 않는다. 길어도 전체 리터럴로 적을 것.
// `**:data-[slot=…]:data-[orientation=…]` = 스크롤바(자손) 중 방향 일치 요소. `data-` variant 로 안정 컴파일.
const SCROLLBAR_INSET_Y =
  '**:data-[slot=scroll-area-scrollbar]:data-[orientation=vertical]:py-2 **:data-[slot=scroll-area-scrollbar]:data-[orientation=vertical]:mr-0.5';
const SCROLLBAR_INSET_X =
  '**:data-[slot=scroll-area-scrollbar]:data-[orientation=horizontal]:px-2 **:data-[slot=scroll-area-scrollbar]:data-[orientation=horizontal]:mb-0.5';

export interface ScrollAreaProps extends ComponentProps<typeof HeritageScrollArea> {
  /**
   * **세로** 스크롤바를 들여쓴다(기본 false) — 트랙 상하 여백 + 우측 보더 이격. 세로 스크롤 영역이 `rounded-*`
   * 보더 컨테이너에 꽉 찰 때만 켤 것(예: 카드 안 목록). 그 외엔 스크롤바가 위아래로 잘린 인상만 준다.
   */
  scrollbarInsetY?: boolean;
  /**
   * **가로** 스크롤바를 들여쓴다(기본 false) — 트랙 좌우 여백 + 하단 보더 이격. 가로 스크롤 영역이 `rounded-*`
   * 보더 컨테이너에 꽉 찰 때만 켤 것(예: 카드 안 표).
   */
  scrollbarInsetX?: boolean;
  /**
   * overscroll 체이닝 차단(기본 true) — 뷰포트 끝 도달 시 부모로 스크롤 전파(바운스)를 막는다.
   *
   * **false 로 꺼야 하는 경우**: base-ui Viewport 는 세로 내용이 안 넘쳐도 **양축 스크롤 컨테이너**라, contain 이면
   * 세로 휠이 부모(페이지)로 전파되지 않고 먹힌다. 가로만 스크롤하고 세로는 페이지에 맡기는 영역(예: 세로 고정
   * 안 한 표)은 false — 내용이 세로로 안 넘칠 때 휠이 페이지로 자연 체이닝된다.
   */
  overscrollContain?: boolean;
}

/**
 * 공용 ScrollArea — heritage ScrollArea + heviton 스크롤 정책 SSOT. overscroll 체이닝 차단은 기본,
 * 스크롤바 인셋은 축별(`scrollbarInsetY`/`scrollbarInsetX`) **opt-in**. 앱 전 스크롤 영역(표·시트 본문·오버레이
 * 목록)이 동일 스크롤 동작을 갖도록 heritage 직수입 대신 이 래퍼를 쓴다(shared/ui barrel 로도 노출).
 * `className` 은 정책 **뒤**에 병합돼 개별 override 가능.
 * (`AppSidebar` 는 자체 튜닝이 있어 heritage 직수입 유지 — 미영향.)
 */
export function ScrollArea({
  scrollbarInsetY = false,
  scrollbarInsetX = false,
  overscrollContain = true,
  className,
  ...props
}: ScrollAreaProps) {
  return (
    <HeritageScrollArea
      className={cn(
        overscrollContain ? OVERSCROLL_CONTAIN : OVERSCROLL_AUTO,
        scrollbarInsetY && SCROLLBAR_INSET_Y,
        scrollbarInsetX && SCROLLBAR_INSET_X,
        className,
      )}
      {...props}
    />
  );
}
